import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { documentKindSchema } from '../enums/document-kind.enum.js'
import { documentStatusSchema } from '../enums/document-status.enum.js'

/**
 * A stored file as the API reports it.
 *
 * `storageKey` is deliberately absent: the bucket layout is an internal detail,
 * and a client that knows object keys is a client that can guess at other
 * people's. Reading a document always goes through a short-lived signed URL
 * issued per request.
 */
export const documentDtoSchema = z.object({
  id: uuidSchema,
  kind: documentKindSchema,
  status: documentStatusSchema,
  fileName: z.string(),
  contentType: z.string(),
  byteSize: z.number().int().nonnegative(),
  title: z.string().nullable(),
  clientId: uuidSchema.nullable(),
  siteId: uuidSchema.nullable(),
  /** Set when this is a photograph of one specific tree. */
  treeId: uuidSchema.nullable(),
  treeProblemId: uuidSchema.nullable(),
  /** Set when this is a photograph of one irrigation record. */
  waterSourceId: uuidSchema.nullable(),
  irrigationTimerId: uuidSchema.nullable(),
  irrigationEquipmentId: uuidSchema.nullable(),
  irrigationProblemId: uuidSchema.nullable(),
  uploadedByName: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
})

export type DocumentDto = z.infer<typeof documentDtoSchema>

export const documentListDtoSchema = z.object({
  documents: z.array(documentDtoSchema),
  total: z.number().int().nonnegative(),
})

export type DocumentListDto = z.infer<typeof documentListDtoSchema>

/**
 * The response to "I want to upload a file": the document row that was created,
 * plus everything the browser needs to PUT the bytes straight to storage.
 */
export const documentUploadTargetDtoSchema = z.object({
  document: documentDtoSchema,
  uploadUrl: z.string(),
  /** Headers that must accompany the PUT for the signature to validate. */
  uploadHeaders: z.record(z.string(), z.string()),
  expiresInSeconds: z.number().int().positive(),
})

export type DocumentUploadTargetDto = z.infer<
  typeof documentUploadTargetDtoSchema
>

/** A short-lived link to read one document. */
export const documentDownloadDtoSchema = z.object({
  downloadUrl: z.string(),
  expiresInSeconds: z.number().int().positive(),
})

export type DocumentDownloadDto = z.infer<typeof documentDownloadDtoSchema>
